import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { resolveApiError, useTranslation } from '@eon/i18n';
import { Badge, Button, Card, CardContent, PageHeader, toast } from '@eon/ui';
import { hooks } from '@eon/api-client';
import { DeviceStatusBadge } from '@/components/status/StatusBadges';
import { CommandFormPanel, type CommandFormState } from '@/components/device-control/CommandFormPanel';
import { CommandSidebar } from '@/components/device-control/CommandSidebar';
import { DEFAULT_COMMAND_ID } from '@/components/device-control/commands';
import { ResponsePanel } from '@/components/device-control/ResponsePanel';
import type { ActionRunner, CallLogEntry, CommandId } from '@/components/device-control/types';

const INITIAL_FORM: CommandFormState = {
  rsConnector: 1,
  rsTag: '',
  rsTx: '',
  rsStopConnector: 1,
  chConnector: 1,
  chType: 'Operative',
  unlockConnector: 1,
  resetType: 'Soft',
  trgMessage: 'Heartbeat',
  trgConnector: '',
  getCfgKeys: '',
  changeCfgKey: '',
  changeCfgValue: '',
  cpConnector: 1,
  cpLimit: 16,
  cpClearId: '',
};

export function DeviceControlPage() {
  const { t } = useTranslation();
  const { deviceId } = useParams({ strict: false }) as { deviceId: string };
  const dq = hooks.useAdminDevice(deviceId);
  const device = dq.data;

  const [selectedCommand, setSelectedCommand] = useState<CommandId>(DEFAULT_COMMAND_ID);
  const [formState, setFormState] = useState<CommandFormState>(INITIAL_FORM);
  const [log, setLog] = useState<CallLogEntry[]>([]);
  const [pending, setPending] = useState<string | null>(null);

  const offline = !device || device.status === 'offline';
  const connectors = device?.connectors ?? [];

  const run = async ({ action, actionLabelKey, request, fn }: ActionRunner) => {
    if (offline) {
      toast.error(t('errors.device.offline'));
      return;
    }
    setPending(selectedCommand);
    const start = performance.now();
    try {
      const response = await fn();
      const ms = Math.round(performance.now() - start);
      setLog((s) =>
        [
          {
            id: `${action}-${Date.now()}`,
            ts: Date.now(),
            action,
            actionLabelKey,
            request,
            response,
            ms,
          },
          ...s,
        ].slice(0, 25),
      );
      toast.success(t('deviceControl.success', { action: t(actionLabelKey), ms }));
    } catch (err) {
      const ms = Math.round(performance.now() - start);
      const message = err instanceof Error ? err.message : String(err);
      setLog((s) =>
        [
          {
            id: `${action}-${Date.now()}`,
            ts: Date.now(),
            action,
            actionLabelKey,
            request,
            error: message,
            ms,
          },
          ...s,
        ].slice(0, 25),
      );
      toast.error(resolveApiError(err, t));
    } finally {
      setPending(null);
    }
  };

  return (
    <>
      <PageHeader
        title={t('deviceControl.pageTitle', {
          name: device?.displayName?.trim() || device?.serialNumber || deviceId,
        })}
        description={device?.serialNumber}
        actions={
          <Button asChild variant="outline" size="sm">
            <Link to="/devices/$deviceId" params={{ deviceId }}>
              <ArrowLeft className="mr-2 h-4 w-4" /> {t('deviceControl.backToDetail')}
            </Link>
          </Button>
        }
      />

      {device ? (
        <Card className="mb-4">
          <CardContent className="flex flex-wrap items-center gap-2 p-4 text-sm">
            <span className="font-medium">{t('common.status')}:</span>
            <DeviceStatusBadge status={device.status} />
            {offline ? (
              <Badge variant="destructive">{t('deviceControl.offlineBadge')}</Badge>
            ) : (
              <Badge variant="success">{t('deviceControl.onlineBadge')}</Badge>
            )}
            {connectors.length > 0 ? (
              <span className="text-muted-foreground ml-auto text-xs">
                {t('deviceControl.connectorCount', { count: connectors.length })}
              </span>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
        <aside className="xl:col-span-3">
          <Card className="xl:sticky xl:top-4">
            <CardContent className="p-3">
              <CommandSidebar
                selected={selectedCommand}
                onSelect={setSelectedCommand}
                disabled={false}
              />
            </CardContent>
          </Card>
        </aside>

        <div className="xl:col-span-5">
          {device ? (
            <CommandFormPanel
              commandId={selectedCommand}
              device={device}
              deviceId={deviceId}
              offline={offline}
              pending={pending}
              state={formState}
              setState={setFormState}
              run={run}
            />
          ) : (
            <Card>
              <CardContent className="text-muted-foreground p-6 text-sm">{t('common.loading')}</CardContent>
            </Card>
          )}
        </div>

        <div className="xl:col-span-4">
          <ResponsePanel log={log} onClear={() => setLog([])} />
        </div>
      </div>
    </>
  );
}
